import { sleep } from '@cldsystems/e3-fe-lib';
import {
  WebshopShoppingCartAddProduct,
  WebshopShoppingCartCommands,
  WebshopShoppingCartCreate,
  WebshopShoppingCartQueries,
  webshopShoppingCartAddProductService,
  webshopShoppingCartCreateService,
} from '@cldsystems/ws-api-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useKeycloak } from '@/providers/keycloak-provider';
import { runtimeHttpClient } from '@/services/services.setup';
import { useStore } from '@/store/zustand/zustand.setup';

interface Props {
  payload: WebshopShoppingCartCreate | WebshopShoppingCartAddProduct;
  createEnabled: boolean;
  addEnabled: boolean;
  shoppingCartId: string | undefined;
  shoppingCartVersion: number | undefined;
}

/**
 * Tracks users for whom a Create request has already been started or completed
 * in this browser session. Survives component remounts (e.g. locale redirect
 * /ro/ → /hu/) so the auto-enabled Create query cannot fire a second time
 * while GetMyIdAndVersion still reports no id.
 */
const shoppingCartCreateStartedForIam = new Set<string>();

const useShoppingCartSyncQuery = ({
  payload,
  createEnabled,
  addEnabled,
  shoppingCartId,
  shoppingCartVersion,
}: Props) => {
  const { keycloak } = useKeycloak();
  const { resetMyCart } = useStore();
  const queryClient = useQueryClient();

  const { company } = useStore();
  const iamId = keycloak?.idTokenParsed?.sub ?? '';
  const [isSettling, setIsSettling] = useState(false);

  const shoppingCartCreateQuery = useQuery({
    queryKey: [WebshopShoppingCartCommands.Create, iamId],
    queryFn: () => {
      shoppingCartCreateStartedForIam.add(iamId);
      return webshopShoppingCartCreateService({ hasSchema: true, httpClient: runtimeHttpClient })({
        payload: { id: '', ...payload, companyId: company.companyId ?? '' },
      });
    },
    enabled: createEnabled && Boolean(iamId) && !shoppingCartCreateStartedForIam.has(iamId),
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Number.POSITIVE_INFINITY,
  });

  const shoppingCartAddQuery = useQuery({
    queryKey: [WebshopShoppingCartCommands.AddProduct, iamId],
    queryFn: () =>
      webshopShoppingCartAddProductService({ hasSchema: true, httpClient: runtimeHttpClient })({
        params: { id: shoppingCartId!, version: shoppingCartVersion?.toString() ?? '' },
        payload: {
          products: payload.products,
          event: {
            name: '',
            id: '',
            sourceUrl: '',
          },
        },
      }),
    enabled: addEnabled,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Number.POSITIVE_INFINITY,
  });

  useEffect(() => {
    if (!keycloak?.authenticated) {
      shoppingCartCreateStartedForIam.clear();
    }
  }, [keycloak?.authenticated]);

  useEffect(() => {
    if (shoppingCartCreateQuery.isError && iamId) {
      shoppingCartCreateStartedForIam.delete(iamId);
    }
  }, [shoppingCartCreateQuery.isError, iamId]);

  useEffect(() => {
    if (shoppingCartCreateQuery.isSuccess || shoppingCartAddQuery.isSuccess) {
      setIsSettling(true);
      resetMyCart();
      void (async () => {
        await sleep(1000);
        await queryClient.invalidateQueries({
          queryKey: [WebshopShoppingCartQueries.GetMyIdAndVersion],
          refetchType: 'all',
        });
        await queryClient.invalidateQueries({
          queryKey: [WebshopShoppingCartQueries.GetById],
          refetchType: 'all',
        });
        setIsSettling(false);
      })();
    }
  }, [shoppingCartCreateQuery.isSuccess, shoppingCartAddQuery.isSuccess, resetMyCart, queryClient]);

  return {
    isSyncing: shoppingCartCreateQuery.isFetching || shoppingCartAddQuery.isFetching || isSettling,
  };
};

export default useShoppingCartSyncQuery;
